import { Component, DestroyRef, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { Chart, ChartConfiguration, Plugin, TooltipModel } from 'chart.js';
import { finalize, Subscription } from 'rxjs';
import { ThemeService } from 'src/app/core/services/theme.service';
import { DateRangeUtils } from 'src/app/shared/components/linear-datetime-range-picker/models/date-range-utils';
import { CustomDropdownControlConfig } from 'src/app/features/reactive-forms-ui/models/custom-dropdown-control-config';
import { IFormControlConfig } from 'src/app/features/reactive-forms-ui/interfaces/form-control-config';
import {
  BreakdownItem,
  CategorizationSummary,
  ChartLegendItem,
  TicketAnalysisFilters,
  TicketAnalysisPage,
} from '../../../models/ticket-analysis.models';
import { TicketAnalysisService } from '../../../services/ticket-analysis.service';
import { ViewPersistenceService } from 'src/app/features/lifecycle-command/services/view-persistence.service';
import {
  TICKET_ANALYSIS_FILTERS_LAYOUT,
  applyFilterDropdowns,
  createTicketAnalysisFilterConfigMap,
  createTicketAnalysisFiltersForm,
  defaultDateFrom,
  extractBaseFilters,
} from '../ticket-analysis-filters.utils';
import {
  CHART_PALETTE_ITEM,
  CHART_PALETTE_SUBTYPE,
  CHART_PALETTE_TYPE,
  DOUGHNUT_CENTER_COLOR,
  DOUGHNUT_LABEL_MAX_CHARS,
  DOUGHNUT_MAX_ITEMS,
  DOUGHNUT_MIN_PCT_LABEL,
} from '../ticket-analysis-chart.constants';

@Component({
  selector: 'ticket-analysis-categorization',
  templateUrl: './ticket-analysis-categorization.component.html',
  styleUrls: ['./ticket-analysis-categorization.component.scss'],
  standalone: false,
})
export class TicketAnalysisCategorizationComponent implements OnInit, OnDestroy {
  loading = false;
  loadingTickets = false;
  error = '';
  summary: CategorizationSummary | null = null;
  ticketPageData: TicketAnalysisPage | null = null;

  currentPage = 1;
  pageSize = 25;
  sortBy = 'dateEntered';
  sortDir: 'asc' | 'desc' = 'desc';

  readonly displayedColumns = ['id', 'dateEntered', 'closedDate', 'contact', 'status', 'summary'];

  typeChartData: ChartConfiguration<'doughnut'>['data'] = { labels: [], datasets: [] };
  subTypeChartData: ChartConfiguration<'doughnut'>['data'] = { labels: [], datasets: [] };
  itemChartData: ChartConfiguration<'doughnut'>['data'] = { labels: [], datasets: [] };
  doughnutOptions: ChartConfiguration<'doughnut'>['options'] = {};

  typeChartLegend: ChartLegendItem[] = [];
  subTypeChartLegend: ChartLegendItem[] = [];
  itemChartLegend: ChartLegendItem[] = [];

  chartFiltersForm = new FormGroup({
    filterByType: new FormControl<string[]>([], { nonNullable: true }),
    filterBySubType: new FormControl<string[]>([], { nonNullable: true }),
    filterByItem: new FormControl<string[]>([], { nonNullable: true }),
  });

  typeChartFilterConfig = new CustomDropdownControlConfig({
    label: 'All',
    placeholder: 'All',
    options: [],
    multiselect: true,
    collapsibleTitle: 'Type',
    reviewLabel: 'Type',
  });
  subTypeChartFilterConfig = new CustomDropdownControlConfig({
    label: 'All',
    placeholder: 'All',
    options: [],
    multiselect: true,
    collapsibleTitle: 'Sub-Type',
    reviewLabel: 'Sub-Type',
  });
  itemChartFilterConfig = new CustomDropdownControlConfig({
    label: 'All',
    placeholder: 'All',
    options: [],
    multiselect: true,
    collapsibleTitle: 'Item',
    reviewLabel: 'Item',
  });

  readonly chartPlugins: Plugin[] = [
    {
      id: 'doughnutCenterLabel',
      beforeDraw(chart: Chart) {
        if ((chart.config as any).type !== 'doughnut') return;
        const { ctx, chartArea } = chart;
        if (!chartArea) return;
        const dataset = chart.data.datasets[0] as any;
        const total = dataset?.fullTotal ?? ((dataset?.data ?? []) as number[]).reduce((s, v) => s + v, 0);
        if (!total) return;
        const cx = (chartArea.left + chartArea.right) / 2;
        const cy = (chartArea.top + chartArea.bottom) / 2;
        ctx.save();
        ctx.font = 'bold 16px Inter, sans-serif';
        ctx.fillStyle = DOUGHNUT_CENTER_COLOR;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(total.toLocaleString(), cx, cy);
        ctx.restore();
      },
    },
    {
      id: 'doughnutSegmentLabels',
      afterDatasetsDraw: (chart: Chart) => {
        if ((chart.config as any).type !== 'doughnut') return;
        const { ctx } = chart;
        const dataset = chart.data.datasets[0];
        if (!dataset) return;
        const values = dataset.data as number[];
        const visibleTotal = values.reduce((s, v) => s + (v || 0), 0);
        const fullTotal = (dataset as any).fullTotal ?? visibleTotal;
        if (!fullTotal) return;

        chart.getDatasetMeta(0).data.forEach((arc: any, i) => {
          const pct = Math.round(((values[i] || 0) / fullTotal) * 100);
          if (pct < DOUGHNUT_MIN_PCT_LABEL) return;
          const props = arc.getProps(['startAngle', 'endAngle', 'innerRadius', 'outerRadius', 'x', 'y'], true);
          const midAngle = props.startAngle + (props.endAngle - props.startAngle) / 2;
          const midRadius = (props.innerRadius + props.outerRadius) / 2;
          ctx.save();
          ctx.font = '400 8px Inter, sans-serif';
          ctx.fillStyle = this.pctLabelColor;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(`${pct}%`, props.x + Math.cos(midAngle) * midRadius, props.y + Math.sin(midAngle) * midRadius);
          ctx.restore();
        });
      },
    },
  ];

  private cardBackground = '#ffffff';
  private pctLabelColor = '#374151';
  private loadSub: Subscription | null = null;
  private loadTicketsSub: Subscription | null = null;
  private tooltipEl: HTMLDivElement | null = null;

  filtersForm = createTicketAnalysisFiltersForm();
  valuesMap: any = this.filtersForm.value;
  filtersConfigMap: Map<string, IFormControlConfig> = createTicketAnalysisFilterConfigMap();
  readonly filtersLayoutConfig = TICKET_ANALYSIS_FILTERS_LAYOUT;

  private readonly STORAGE_KEY = 'ticket_analysis_categorization';

  constructor(
    private readonly ticketAnalysisService: TicketAnalysisService,
    private readonly themeService: ThemeService,
    private readonly destroyRef: DestroyRef,
    private viewPersistenceService: ViewPersistenceService,
  ) {}

  ngOnDestroy(): void {
    Chart.unregister(...this.chartPlugins);
    this.tooltipEl?.remove();
  }

  ngOnInit(): void {
    this.buildChartOptions();
    this.themeService.themeSubject$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.buildChartOptions();
      if (this.summary) this.applySummary(this.summary);
    });
    this.restoreFilters();
    this.ticketAnalysisService.exportRequested$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      const { filterByType, filterBySubType, filterByItem } = this.chartFiltersForm.value;
      this.ticketAnalysisService.startExport(extractBaseFilters(this.valuesMap, this.filtersConfigMap), {
        view: 'categorization',
        filterByType: filterByType?.length ? filterByType.join(',') : undefined,
        filterBySubType: filterBySubType?.length ? filterBySubType.join(',') : undefined,
        filterByItem: filterByItem?.length ? filterByItem.join(',') : undefined,
      });
    });
    this.loadDropdowns();
    this.load();
  }

  updateValuesMap(valuesMap: any): void {
    this.valuesMap = valuesMap;
  }

  applyFilters(): void {
    this.saveFilters();
    this.load();
  }

  clearFilters(): void {
    this.filtersForm.reset({
      dateRange: new DateRangeUtils().buildLastMonthDateRange(),
      site: null,
      board: null,
      contactName: null,
      status: null,
      source: null,
    });
    this.valuesMap = this.filtersForm.value;
    this.chartFiltersForm.reset({ filterByType: [], filterBySubType: [], filterByItem: [] }, { emitEvent: false });
    this.viewPersistenceService.clearFilters(this.STORAGE_KEY);
    this.load();
  }

  clearChartFilter(control: 'filterByType' | 'filterBySubType' | 'filterByItem'): void {
    this.chartFiltersForm.get(control)?.setValue([], { emitEvent: false });
    this.load();
  }

  get noTicketData(): boolean {
    return !this.loadingTickets && !!this.ticketPageData && this.ticketPageData.total === 0;
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.loadTickets();
  }

  onSortChange(sort: Sort): void {
    this.sortBy = sort.active || 'dateEntered';
    this.sortDir = (sort.direction as 'asc' | 'desc') || 'desc';
    this.currentPage = 1;
    this.loadTickets();
  }

  loadTickets(): void {
    this.loadTicketsSub?.unsubscribe();
    this.loadingTickets = true;
    this.loadTicketsSub = this.ticketAnalysisService
      .getTickets(this.buildFilters(), this.currentPage, this.pageSize, this.sortBy, this.sortDir)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.loadingTickets = false)),
      )
      .subscribe({
        next: (data) => (this.ticketPageData = data),
        error: () => {
          this.ticketPageData = null;
          this.error = 'Failed to load categorization data.';
        },
      });
  }

  load(): void {
    this.currentPage = 1;
    this.loadSummary();
    this.loadTickets();
  }

  private loadSummary(): void {
    this.loadSub?.unsubscribe();
    this.loading = true;
    this.error = '';
    this.loadSub = this.ticketAnalysisService
      .getCategorization(this.buildFilters())
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.loading = false)),
      )
      .subscribe({
        next: (data) => {
          this.applySummary(data);
          this.refreshChartFilterOptions(data);
        },
        error: () => {
          this.summary = null;
          this.ticketPageData = null;
          this.error = 'Failed to load categorization data.';
        },
      });
  }

  private applySummary(data: CategorizationSummary): void {
    this.summary = data;
    const { filterByType, filterBySubType, filterByItem } = this.chartFiltersForm.value;

    const typeItems = filterByType?.length
      ? data.byServiceType.filter((i) => filterByType.includes(i.name))
      : data.byServiceType;
    const subTypeItems = filterBySubType?.length
      ? data.bySubType.filter((i) => filterBySubType.includes(i.name))
      : data.bySubType;
    const itemItems = filterByItem?.length ? data.byItem.filter((i) => filterByItem.includes(i.name)) : data.byItem;

    this.typeChartData = this.buildChartData(typeItems, CHART_PALETTE_TYPE);
    this.subTypeChartData = this.buildChartData(subTypeItems, CHART_PALETTE_SUBTYPE);
    this.itemChartData = this.buildChartData(itemItems, CHART_PALETTE_ITEM);
    this.typeChartLegend = this.buildLegendItems(typeItems, CHART_PALETTE_TYPE);
    this.subTypeChartLegend = this.buildLegendItems(subTypeItems, CHART_PALETTE_SUBTYPE);
    this.itemChartLegend = this.buildLegendItems(itemItems, CHART_PALETTE_ITEM);
  }

  private loadDropdowns(): void {
    this.ticketAnalysisService.getFilters().subscribe({
      next: (response) => {
        this.filtersConfigMap = applyFilterDropdowns(response, this.filtersConfigMap);
      },
    });
  }

  private buildFilters(): TicketAnalysisFilters {
    const { filterByType, filterBySubType, filterByItem } = this.chartFiltersForm.value;
    return {
      ...extractBaseFilters(this.valuesMap, this.filtersConfigMap),
      filterByType: filterByType?.length ? filterByType.join(',') : undefined,
      filterBySubType: filterBySubType?.length ? filterBySubType.join(',') : undefined,
      filterByItem: filterByItem?.length ? filterByItem.join(',') : undefined,
    };
  }

  private refreshChartFilterOptions(data: CategorizationSummary): void {
    const toOptions = (items: BreakdownItem[]) => items.map((i) => ({ name: i.name, value: i.name }));
    this.typeChartFilterConfig = new CustomDropdownControlConfig({
      ...this.typeChartFilterConfig,
      options: toOptions(data.byServiceType),
    });
    this.subTypeChartFilterConfig = new CustomDropdownControlConfig({
      ...this.subTypeChartFilterConfig,
      options: toOptions(data.bySubType),
    });
    this.itemChartFilterConfig = new CustomDropdownControlConfig({
      ...this.itemChartFilterConfig,
      options: toOptions(data.byItem),
    });
  }

  private buildLegendItems(items: BreakdownItem[], palette: readonly string[]): ChartLegendItem[] {
    return items.slice(0, DOUGHNUT_MAX_ITEMS).map((item, idx) => ({
      label:
        item.name.length > DOUGHNUT_LABEL_MAX_CHARS ? item.name.slice(0, DOUGHNUT_LABEL_MAX_CHARS) + '…' : item.name,
      color: palette[idx % palette.length],
      count: item.ticketCount,
    }));
  }

  private buildChartData(items: BreakdownItem[], palette: readonly string[]): ChartConfiguration<'doughnut'>['data'] {
    const top = items.slice(0, DOUGHNUT_MAX_ITEMS);
    const fullTotal = items.reduce((sum, i) => sum + i.ticketCount, 0);
    return {
      labels: top.map((i) => i.name),
      datasets: [
        {
          data: top.map((i) => i.ticketCount),
          backgroundColor: top.map((_, idx) => palette[idx % palette.length]),
          hoverBackgroundColor: top.map((_, idx) => palette[idx % palette.length] + 'cc'),
          borderColor: this.cardBackground,
          hoverBorderColor: this.cardBackground,
          borderWidth: 0.5,
          hoverOffset: 4,
          fullTotal,
        } as any,
      ],
    };
  }

  private buildChartOptions(): void {
    const isDark = this.themeService.isInDarkMode();
    this.cardBackground = isDark ? '#1b212d' : '#ffffff';
    this.pctLabelColor = isDark ? '#e5e7eb' : '#374151';
    this.doughnutOptions = {
      responsive: true,
      maintainAspectRatio: false,
      cutout: '65%',
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false, external: (context) => this.renderTooltip(context) },
      },
    };
  }

  private getOrCreateTooltipEl(): HTMLDivElement {
    if (!this.tooltipEl) {
      this.tooltipEl = document.createElement('div');
      this.tooltipEl.className = 'cat__chart-tooltip';
      document.body.appendChild(this.tooltipEl);
    }
    return this.tooltipEl;
  }

  private renderTooltip(context: { chart: Chart; tooltip: TooltipModel<'doughnut'> }): void {
    const { chart, tooltip } = context;
    const el = this.getOrCreateTooltipEl();

    if (tooltip.opacity === 0) {
      el.style.opacity = '0';
      return;
    }

    const dataPoint = tooltip.dataPoints?.[0];
    if (dataPoint) {
      const count = Number(dataPoint.raw) || 0;
      el.innerHTML = '';
      const nameEl = document.createElement('div');
      nameEl.className = 'cat__chart-tooltip-name';
      nameEl.textContent = dataPoint.label ?? '';
      const countEl = document.createElement('div');
      countEl.className = 'cat__chart-tooltip-count';
      countEl.textContent = `${count.toLocaleString()} ticket${count === 1 ? '' : 's'}`;
      el.append(nameEl, countEl);
    }

    const rect = chart.canvas.getBoundingClientRect();
    el.style.opacity = '1';
    el.style.left = `${rect.left + tooltip.caretX}px`;
    el.style.top = `${rect.top + tooltip.caretY}px`;
  }

  private saveFilters(): void {
    const dateRange = this.valuesMap?.dateRange as { start: Date; end: Date } | null;
    this.viewPersistenceService.saveFilters(this.STORAGE_KEY, {
      ...this.valuesMap,
      dateRange: dateRange
        ? { start: dateRange.start?.toISOString?.() ?? null, end: dateRange.end?.toISOString?.() ?? null }
        : null,
    });
  }

  private restoreFilters(): void {
    const saved = this.viewPersistenceService.loadFilters(this.STORAGE_KEY);
    if (!saved) return;
    const rawRange = saved['dateRange'] as { start: string; end: string } | null;
    const start = rawRange?.start ? new Date(rawRange.start) : defaultDateFrom();
    const end = rawRange?.end ? new Date(rawRange.end) : new Date();
    const asTree = (v: unknown) =>
      v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, any>) : null;
    const restored = {
      dateRange: { start, end },
      contactName: asTree(saved['contactName']),
      board: asTree(saved['board']),
      site: asTree(saved['site']),
      status: asTree(saved['status']),
      source: asTree(saved['source']),
    };
    this.filtersForm.patchValue(restored);
    this.valuesMap = restored;
  }
}
